// src/components/BlogAdmin.js
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { ChevronLeftIcon, UserPlusIcon } from '@heroicons/react/24/outline';

export default function BlogAdmin({ user, onBack, isAdmin }) {
  const [authors, setAuthors] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchEmail, setSearchEmail] = useState('');

  // Check if current user is admin
  const checkAdminStatus = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('blog_authors')
        .select('is_admin')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking admin status:', error);
      }

      return data?.is_admin || false;
    } catch (err) {
      console.error('Error checking admin:', err);
      return false;
    }
  }, [user.id]);

  // Fetch all blog authors
  const fetchAuthors = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('blog_authors')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setAuthors(data || []);
    } catch (err) {
      console.error('Error fetching authors:', err);
      setError('Failed to load authors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const adminStatus = await checkAdminStatus();
      if (!adminStatus) {
        setError('You do not have admin permissions');
        setLoading(false);
        return;
      }
      fetchAuthors();
    };
    
    init();
  }, [checkAdminStatus, fetchAuthors]);

  // Search for user by email
  const handleSearch = async () => {
    if (!searchEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    setError('');
    setSuccess('');

    try {
      // Use the database function to add author
      const { data, error } = await supabase
        .rpc('add_blog_author', {
          search_email: searchEmail.trim(),
          make_admin: false
        });

      if (error) throw error;

      // Check the result
      if (data.success) {
        setSuccess(data.message);
        setSearchEmail('');
        fetchAuthors();
      } else {
        setError(data.error);
      }
    } catch (err) {
      console.error('Error adding author:', err);
      setError(err.message || 'Failed to add author');
    }
  };

  // Remove author
  const handleRemoveAuthor = async (authorId, authorEmail) => {
    if (!window.confirm(`Remove ${authorEmail} as a blog author?`)) return;

    setError('');
    setSuccess('');

    try {
      const { error } = await supabase
        .from('blog_authors')
        .delete()
        .eq('id', authorId);

      if (error) throw error;

      setSuccess(`Removed ${authorEmail} as a blog author`);
      fetchAuthors();
    } catch (err) {
      console.error('Error removing author:', err);
      setError('Failed to remove author');
    }
  };

  // Toggle admin status
  const handleToggleAdmin = async (authorId, currentStatus, authorEmail) => {
    const action = currentStatus ? 'remove admin rights from' : 'grant admin rights to';
    if (!window.confirm(`${action} ${authorEmail}?`)) return;

    setError('');
    setSuccess('');

    try {
      const { error } = await supabase
        .from('blog_authors')
        .update({ is_admin: !currentStatus })
        .eq('id', authorId);

      if (error) throw error;

      setSuccess(`Updated admin status for ${authorEmail}`);
      fetchAuthors();
    } catch (err) {
      console.error('Error updating admin status:', err);
      setError('Failed to update admin status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center">
          <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <button
          onClick={onBack}
          className="flex items-center text-purple-300 hover:text-white transition-colors"
        >
          <ChevronLeftIcon className="h-5 w-5 mr-2" />
          Back
        </button>

        <div className="bg-red-500/20 border border-red-500/50 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-bold text-white mb-2">Access Denied</h3>
          <p className="text-red-200">You do not have admin permissions</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center text-purple-300 hover:text-white transition-colors"
        >
          <ChevronLeftIcon className="h-5 w-5 mr-2" />
          Back to Blog
        </button>
      </div>

      {/* Add Author */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
          <UserPlusIcon className="h-6 w-6 mr-3" />
          Add Blog Author
        </h2>

        {error && (
          <div className="mb-4 bg-red-500/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-500/20 border border-green-500/50 rounded-lg p-3">
            <p className="text-green-200 text-sm">{success}</p>
          </div>
        )}

        <div className="flex gap-3">
          <input
            type="email"
            className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            placeholder="Enter user email address..."
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button
            onClick={handleSearch}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
          >
            Add Author
          </button>
        </div>

        <p className="text-purple-300 text-sm mt-3">
          Search for a user by their email address to grant them blog author permissions
        </p>
      </div>

      {/* Current Authors */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
        <h3 className="text-xl font-bold text-white mb-6">
          Current Blog Authors ({authors.length})
        </h3>

        {authors.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-purple-300">No blog authors yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {authors.map((author) => (
              <div
                key={author.id}
                className="bg-white/10 border border-white/10 rounded-lg p-4 flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-white">{author.email}</span>
                    {author.is_admin && (
                      <span className="px-2 py-1 bg-purple-500/30 text-purple-300 text-xs rounded-full border border-purple-500/50">
                        Admin
                      </span>
                    )}
                    {author.user_id === user.id && (
                      <span className="px-2 py-1 bg-blue-500/30 text-blue-300 text-xs rounded-full border border-blue-500/50">
                        You
                      </span>
                    )}
                  </div>
                  <p className="text-purple-400 text-xs mt-1">
                    Added: {new Date(author.created_at).toLocaleDateString()}
                  </p>
                </div>

                <div className="flex gap-2">
                  {author.user_id !== user.id && (
                    <>
                      <button
                        onClick={() => handleToggleAdmin(author.id, author.is_admin, author.email)}
                        className="text-blue-300 hover:text-blue-200 text-sm px-3 py-1 rounded bg-blue-500/20"
                      >
                        {author.is_admin ? 'Remove Admin' : 'Make Admin'}
                      </button>
                      <button
                        onClick={() => handleRemoveAuthor(author.id, author.email)}
                        className="text-red-300 hover:text-red-200 text-sm px-3 py-1 rounded bg-red-500/20"
                      >
                        Remove
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Admin Info */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
        <h4 className="text-lg font-bold text-white mb-4"> Admin Information</h4>
        <ul className="space-y-2 text-purple-200 text-sm">
          <li>• Blog authors can create, edit, and publish blog posts</li>
          <li>• Admins can manage who has blog author permissions</li>
          <li>• Authors can only edit and delete their own posts</li>
          <li>• You cannot remove yourself as an author</li>
          <li>• At least one admin should always exist</li>
        </ul>
      </div>
    </div>
  );
}